import React, { useRef, useEffect, useMemo } from 'react';
import { Modal, Button, Row, Col } from 'antd';
import { Form } from 'formik-antd';
import { isNil, flatMap, isEqual } from 'lodash';
import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';

import cx from 'classnames';

import {
  BOUserStatusEnum,
  FullBOUserResponseModel,
  BOUsersPriceAgreementModel,
  StageStatusEnum,
  PriceAgreementEEInfoModel,
} from 'api/adminApiClient';
import {
  createRecruiterPriceAgreement,
  changeRecruiterPriceAgreement,
  editPriceAgreement,
} from 'redux/reducers/recruiterSlice';
import BaseAlert from 'components/wrappers/BaseAlert';
import FormItem from 'components/formik/FormItem';
import InputNumber from 'components/formik/InputNumber';
import Radio from 'components/formik/Radio';
import SubmitButton from 'components/formik/SubmitButton';
import { getAllStates } from 'redux/reducers/regionSlice';
import {
  isStatesLoadingSelector,
  statesSelector,
} from 'redux/selectors/regionSelectors';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import PricingAgreementUploader from './PricingAgreementUploader';
import {
  BOUsersPriceAgreementEditModel,
  ExtendedNamedAttachmentModel,
  FundingTypeEnum,
  StateEntity,
} from 'api/backofficeApiClient';
import { BaseModalFooter, BaseModal } from 'components/wrappers/BaseModal';
import { StepChangeRequestModel } from 'utils/profileProgressUtils/generalProgressUtils';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { countryUSAid } from 'constants/index';
import { AgreementFileValue } from '../ClientModal/ServiceAgreement/AgreementUploader/AgreementUploader';
import { BaseFileYupObject } from 'components/formik/FormikFileUpload';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { getAllWorkCodes } from 'redux/reducers/assignmentSlice';

import styles from './PricingAgreementModal.module.less';
import { getFundingTypeLabel } from 'utils/clientUtils';

export enum PricingAgreementType {
  Create = 0,
  Change = 1,
  AdminEdit = 2,
}

type Props = {
  onClose: () => void;
  recruiter?: FullBOUserResponseModel;
  agreementType: PricingAgreementType;
  onAgreementSent: (stageData: StepChangeRequestModel) => void;
};

interface PricingSet {
  w2Price: number | null;
  placementStates: number[];
  // workCodes: string[];
}

const emptyPricingSet: PricingSet = {
  w2Price: null,
  placementStates: [],
  // workCodes: [],
};

interface PricingAgreementFormValues {
  agreementType: PricingAgreementType;
  pricingSets: Array<PricingSet>;
  icPrice: number | null;
  fundingType: FundingTypeEnum | null;
  agreementPath?: AgreementFileValue[];
}

const isAgreementRequired = (agreementType: PricingAgreementType) =>
  agreementType === PricingAgreementType.Create ||
  agreementType === PricingAgreementType.AdminEdit;

const pricingSetValidationSchema = {
  w2Price: Yup.number()
    .nullable()
    .required('Field is required')
    .min(0, 'W2 price should be from 0 to 100')
    .max(100, 'W2 price should be from 0 to 100'),
  placementStates: Yup.array().of(Yup.number()).min(1, 'Field is required'),
  // workCodes: Yup.array()
  //   .of(
  //     Yup.string().trim().max(25, 'Work code should be 25 characters maximum'),
  //   )
  //   .min(1, 'Field is required'),
};

const validationSchema = Yup.object({
  pricingSets: Yup.array().of(Yup.object(pricingSetValidationSchema)),
  icPrice: Yup.number()
    .nullable()
    .required('Field is required')
    .min(0, 'IC price should be from 0 to 100')
    .max(100, 'IC price should be from 0 to 100'),
  fundingType: Yup.number().nullable(),
  agreementPath: Yup.array()
    .of(BaseFileYupObject)
    .when('agreementType', {
      is: isAgreementRequired,
      then: Yup.array().of(BaseFileYupObject).min(1, 'Field is required'),
    }),
});

const getPricingSetStates = (
  states: StateEntity[],
  setIndex: number,
  formPricingSets: Array<PricingSet>,
) => {
  const statesInOtherSets = flatMap(formPricingSets, (set, index) =>
    setIndex === index ? [] : set.placementStates,
  );
  return states.filter(
    (state) => !statesInOtherSets.includes(state.id as number),
  );
};

// const getFilteredWorkCodesList = (
//   workCodes: string[],
//   setIndex: number,
//   formPricingSets: Array<PricingSet>,
// ) => {
//   const workCodesInOtherSets = flatMap(formPricingSets, (set, index) =>
//     setIndex === index ? [] : set.workCodes,
//   );
//
//   return workCodes.filter((item) => !workCodesInOtherSets.includes(item));
// };

export default function PricingAgreementModal(props: Props) {
  const { onClose, onAgreementSent, recruiter, agreementType } = props;
  const isAdmin = useAppSelector(isAdminSelector);
  // const workCodes = useAppSelector(workCodesSelector);
  // const isWorkCodesLoading = useAppSelector(isWorkCodesLoadingSelector);
  const dispatch = useAppDispatch();
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const formRef: any = useRef();
  const initialValues = getInitialValues();
  const pricingAgreementDefaultFilesRef = useRef(
    initialValues?.agreementPath || [],
  );
  const isAdminEdit = agreementType === PricingAgreementType.AdminEdit;

  const filteredStates = useMemo(
    () => states.filter((state) => state.countryId === countryUSAid),
    [states],
  );

  useEffect(() => {
    dispatch(getAllStates());
    dispatch(getAllWorkCodes(isAdmin));
  }, [dispatch, isAdmin]);

  function getInitialValues(): PricingAgreementFormValues {
    if (
      agreementType === PricingAgreementType.Create ||
      agreementType === PricingAgreementType.Change
    ) {
      return {
        agreementType,
        pricingSets: [emptyPricingSet],
        icPrice: null,
        fundingType: null,
        agreementPath: [],
      };
    }

    const pricingSets: Array<PricingSet> = recruiter?.businessInfo
      ?.priceAgreement?.w2InfoList?.length
      ? (recruiter?.businessInfo?.priceAgreement?.w2InfoList).map((w2Info) => ({
          w2Price: !isNil(w2Info.w2Price) ? w2Info.w2Price : null,
          placementStates: (w2Info.placementStates || []).map(
            (state) => state.id as number,
          ),
          workCodes: w2Info.workCodes?.length ? w2Info.workCodes : [],
        }))
      : [emptyPricingSet];
    const icPrice = !isNil(recruiter?.businessInfo?.priceAgreement?.icPrice)
      ? (recruiter?.businessInfo?.priceAgreement?.icPrice as number)
      : null;
    const fundingType =
      recruiter?.businessInfo?.priceAgreement?.fundingType ?? null;
    const agreementPath = recruiter?.businessInfo?.priceAgreement
      ?.supportingFiles?.length
      ? recruiter?.businessInfo?.priceAgreement.supportingFiles.map(
          (file: ExtendedNamedAttachmentModel) => ({
            fileName: file.fileName || '',
            s3Key: file.fileKey || '',
            createdDate: file?.createdDate || undefined,
          }),
        )
      : [];

    return {
      agreementType,
      pricingSets,
      icPrice,
      fundingType,
      agreementPath,
    };
  }

  const getSubmitData = (values: PricingAgreementFormValues) => {
    const { pricingSets, icPrice, agreementPath, fundingType } = values;

    if (!pricingSets.length || isNil(icPrice)) return;

    const w2InfoList: PriceAgreementEEInfoModel[] = pricingSets.map((item) => ({
      w2Price: item.w2Price as number,
      placementStateIds: item.placementStates,
      // workCodes: item.workCodes.map((code) => code.trim()),
    }));

    const createData: BOUsersPriceAgreementModel = {
      w2InfoList,
      icPrice,
      fundingType: fundingType ?? undefined,
      companyId: recruiter?.companyId,
      supportingFiles: agreementPath?.length
        ? agreementPath.map((file) => ({
            fileKey: file.s3Key,
            fileName: file.fileName,
            createdDate:
              initialValues.agreementPath?.find(
                (initialFile) => initialFile.s3Key === file.s3Key,
              )?.createdDate || undefined,
          }))
        : undefined,
    };
    const changeData: BOUsersPriceAgreementEditModel = {
      eeInfoList: w2InfoList,
      icPrice,
    };

    switch (agreementType) {
      case PricingAgreementType.Create:
      case PricingAgreementType.AdminEdit:
        return createData;

      case PricingAgreementType.Change:
        return changeData;
    }
  };

  const getAction = () => {
    switch (agreementType) {
      case PricingAgreementType.Create:
        return createRecruiterPriceAgreement;

      case PricingAgreementType.Change:
        return changeRecruiterPriceAgreement;

      case PricingAgreementType.AdminEdit:
        return editPriceAgreement;
    }
  };

  const handleSubmit = async (values: PricingAgreementFormValues) => {
    // https://mybasepay.atlassian.net/browse/MBO-1832
    // Do not send pricing agreement if the data hasn't changed
    if (isAdminEdit) {
      const areValuesEqual = isEqual(initialValues, values);

      if (areValuesEqual) return onClose();
    }

    const data = getSubmitData(values);
    const correctAction = getAction();

    if (!data) return;

    const result = await dispatch(correctAction(data));
    if (correctAction.fulfilled.match(result)) {
      await onAgreementSent({
        stage: BOUserStatusEnum.PriceAgreementSent,
        stageStatus: StageStatusEnum.Completed,
      });
      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleCloseModal = () => {
    if (formRef.current.isSubmitting) return;

    if (formRef.current.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  return (
    <BaseModal
      title="Pricing agreement"
      open={true}
      closable={true}
      onCancel={handleCloseModal}
      destroyOnClose
      maxWidth={468}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        innerRef={formRef}
      >
        {(formik) => (
          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              <div>
                <FieldArray
                  name="pricingSets"
                  render={(arrayHelpers) => (
                    <>
                      {!!formik.values.pricingSets.length &&
                        formik.values.pricingSets.map((set, index: number) => (
                          <div key={index}>
                            <Row gutter={20}>
                              <Col xs={24}>
                                <FormItem
                                  label="W2 price % *"
                                  name={`pricingSets.${index}.w2Price`}
                                  data-id={`w2PriceField-${index}`}
                                >
                                  <InputNumber
                                    precision={2}
                                    name={`pricingSets.${index}.w2Price`}
                                    placeholder="Enter W2 price"
                                    className={styles.input}
                                  />
                                </FormItem>
                              </Col>
                            </Row>

                            {/*<Row gutter={20}>
                              <Col xs={24}>
                                <FormItem
                                  name={`pricingSets.${index}.workCodes`}
                                  label="WC Codes *"
                                >
                                  <FormikSelectWithLoading
                                    mode="tags"
                                    name={`pricingSets.${index}.workCodes`}
                                    placeholder="Select worker codes"
                                    isLoading={isWorkCodesLoading}
                                  >
                                    {getFilteredWorkCodesList(
                                      workCodes,
                                      index,
                                      formik.values.pricingSets,
                                    ).map((code) => (
                                      <FormikSelectWithLoading.Option
                                        key={`select-worker-code-${code}`}
                                        value={code}
                                      >
                                        {code}
                                      </FormikSelectWithLoading.Option>
                                    ))}
                                  </FormikSelectWithLoading>
                                </FormItem>
                              </Col>
                            </Row>*/}

                            <Row gutter={20}>
                              <Col xs={24}>
                                <FormItem
                                  label="States that W2 price applies to *"
                                  name={`pricingSets.${index}.placementStates`}
                                  data-id={`placementStatesField-${index}`}
                                >
                                  <FormikSelectWithLoading
                                    isLoading={isStatesLoading}
                                    name={`pricingSets.${index}.placementStates`}
                                    showSearch
                                    mode="multiple"
                                    placeholder="Select states"
                                    optionFilterProp="children"
                                    key={`set-${index}-${
                                      getPricingSetStates(
                                        states,
                                        index,
                                        formik.values.pricingSets,
                                      ).length
                                    }`}
                                  >
                                    {getPricingSetStates(
                                      filteredStates,
                                      index,
                                      formik.values.pricingSets,
                                    ).map((state) => (
                                      <FormikSelectWithLoading.Option
                                        key={`select-place-${state.id}`}
                                        value={state.id || -1}
                                      >
                                        {state.name}
                                      </FormikSelectWithLoading.Option>
                                    ))}
                                  </FormikSelectWithLoading>
                                </FormItem>
                              </Col>
                            </Row>

                            <Col flex="0 0 auto">
                              <div className={styles.actionButtons}>
                                {formik.values.pricingSets.length - 1 ===
                                  index && (
                                  <Button
                                    className={cx('borderless-btn', styles.btn)}
                                    onClick={() =>
                                      arrayHelpers.push(emptyPricingSet)
                                    }
                                    icon={
                                      <span className="icon icon-plus-filled"></span>
                                    }
                                  />
                                )}
                                {formik.values.pricingSets.length > 1 && (
                                  <Button
                                    className={cx('borderless-btn', styles.btn)}
                                    onClick={() => arrayHelpers.remove(index)}
                                    icon={
                                      <span className="icon icon-closed-filled" />
                                    }
                                  />
                                )}
                              </div>
                            </Col>
                          </div>
                        ))}
                    </>
                  )}
                />
              </div>

              <Row gutter={20}>
                <Col xs={24}>
                  <FormItem
                    data-id="icPriceField"
                    label="IC price % *"
                    name="icPrice"
                  >
                    <InputNumber
                      name="icPrice"
                      placeholder="Enter IC price"
                      precision={2}
                    />
                  </FormItem>
                </Col>
              </Row>
              <Row gutter={20}>
                <Col xs={24}>
                  <FormItem label="Funding type" name="fundingType">
                    <Radio.Group name="fundingType">
                      <Radio
                        name="fundingTypeFundedRadio"
                        value={FundingTypeEnum.Funded}
                      >
                        {getFundingTypeLabel(FundingTypeEnum.Funded)}
                      </Radio>

                      <Radio
                        name="fundingTypeSelfFundedRadio"
                        value={FundingTypeEnum.SelfFunded}
                      >
                        {getFundingTypeLabel(FundingTypeEnum.SelfFunded)}
                      </Radio>

                      <Radio
                        name="fundingTypeBothRadio"
                        value={FundingTypeEnum.Both}
                      >
                        {getFundingTypeLabel(FundingTypeEnum.Both)}
                      </Radio>
                    </Radio.Group>
                  </FormItem>
                </Col>
              </Row>

              {isAgreementRequired(agreementType) && (
                <>
                  <div className="modal-title">Upload agreement *</div>

                  <Row gutter={[0, 40]} justify="center">
                    <Col xs={24}>
                      <div className="upload-wrapper">
                        <FormItem
                          name="agreementPath"
                          showValidateError={false}
                        >
                          <PricingAgreementUploader
                            defaultFile={
                              pricingAgreementDefaultFilesRef.current
                            }
                          />
                        </FormItem>
                      </div>
                    </Col>
                  </Row>
                </>
              )}
            </div>

            <BaseModalFooter>
              <div>
                <Button
                  data-id="closePricingAgreementModalBtn"
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>

                <SubmitButton
                  type="primary"
                  data-id="submitPricingAgreementBtn"
                >
                  Send
                </SubmitButton>
              </div>
            </BaseModalFooter>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
}
